import {
   convertDetachedDayToUtcDateWithoutTimezoneOffset,
   getAttachedDate,
} from "@laborchart-modules/common/dist/datetime/date";
import { Avatar, Flex, Pill, Typography, colors, useI18nContext } from "@procore/core-react";
import { Excavator, Folder, ListArrowDownRight, CopilotBranded } from "@procore/core-icons";
import React from "react";
import {
   getDatesRangeString,
   formatTimeString,
   formatPhoneNumberForTaskCard,
   getDailyRateAndBurn,
} from "../utils";
import { AddressDisplay } from "./address-display";
import { TagDisplay } from "./tag-display";
import "./index.css";
import { BoardsTaskType } from "@laborchart-modules/lc-core-api/dist/api/assignments/load-boards";
import { getInitials } from "../../tearsheets/project/helpers";
import { useBoardsConfig } from "../hooks/use-boards-config";
import { useProfilePicture } from "@/react/shared/hooks/use-profile-picture";
import { authManager } from "@/lib/managers/auth-manager";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import { useTaskUpdate } from "../hooks/use-task-update";

// Add CSS variables
const styleTag = document.createElement("style");
styleTag.innerHTML = `:root { --procore-gray40: ${colors.gray60}; }`;
document.head.appendChild(styleTag);

export function TaskCardHeaderContent({
   taskRecord,
   assignmentCount,
}: {
   taskRecord: any;
   assignmentCount?: number;
}) {
   const I18n = useI18nContext();
   useTaskUpdate(taskRecord);
   const config = useBoardsConfig();

   // Get profile picture - hook must be called at top level
   const resourcePhotoId = taskRecord.getData("resource_photo_url");
   const resolvedProfilePicUrl = useProfilePicture(resourcePhotoId);
   const taskType = taskRecord.getData("type");

   if (taskType === BoardsTaskType.REQUEST) {
      const startDay = taskRecord.getData("start_day");
      const requestStartDate = convertDetachedDayToUtcDateWithoutTimezoneOffset(startDay);
      const isFutureRequest = requestStartDate?.getTime() >= new Date().getTime();
      return (
         <Flex
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            padding="sm"
            style={{ width: "100%" }}
            className="task-card-header-request"
         >
            <Pill color="blue">{I18n.t("views.company.workforce_planning.request.request")}</Pill>
            {isFutureRequest && (
               <CopilotBranded
                  size="md"
                  className="assist-icon"
                  aria-label="Assist"
                  aria-hidden={false}
               />
            )}
         </Flex>
      );
   } else if (taskType === BoardsTaskType.CATEGORY) {
      const depth = taskRecord.getData("depth") ?? 0;
      const countColors =
         depth === 0
            ? { bg: colors.gray40, text: "white" }
            : depth === 1
            ? { bg: colors.gray60, text: "white" }
            : depth === 2
            ? { bg: colors.gray85, text: "black" }
            : { bg: colors.gray90, text: "black" };
      return (
         <div className="task-card-category-header">
            <Flex alignItems="center" justifyContent="space-between">
               <Flex alignItems="center" gap="sm">
                  {depth === 0 ? (
                     <Folder size="sm" style={{ color: "currentColor" }} />
                  ) : (
                     <ListArrowDownRight size="sm" style={{ color: "currentColor" }} />
                  )}
                  <Typography intent="body">{taskRecord.getData("name")}</Typography>
               </Flex>
               <span
                  style={{
                     backgroundColor: countColors.bg,
                     color: countColors.text,
                     borderRadius: 9999,
                     padding: "0 6px",
                     fontSize: 11,
                     fontWeight: 600,
                     lineHeight: "18px",
                     marginRight: 8,
                     whiteSpace: "nowrap",
                  }}
               >
                  {assignmentCount}
               </span>
            </Flex>
         </div>
      );
   } else if (taskType === BoardsTaskType.ASSIGNMENT) {
      const name = taskRecord.getData("name");
      const jobTitle = taskRecord.getData("job_title_name");
      const employeeNumber = taskRecord.getData("employee_number");
      return (
         <div className="task-card-header-content">
            <Flex
               alignItems="flex-start"
               justifyContent="flex-start"
               gap="sm"
               style={{ minWidth: 0 }}
            >
               {config.showResourcePhoto && (
                  <Avatar size="sm">
                     {resolvedProfilePicUrl ? (
                        <Avatar.Portrait imageUrl={resolvedProfilePicUrl} />
                     ) : (
                        <Avatar.Label>{getInitials(name)}</Avatar.Label>
                     )}
                  </Avatar>
               )}
               <div style={{ minWidth: 0 }}>
                  <Typography style={{ minWidth: 0 }}>{name}</Typography>
                  {config.showJobTitle && (
                     <Typography className="task-card-header-detail" intent="small">
                        {jobTitle || "--"}
                     </Typography>
                  )}
                  {config.showEmployeeID && (
                     <Typography className="task-card-header-detail" intent="small">
                        {employeeNumber || "--"}
                     </Typography>
                  )}
               </div>
            </Flex>
         </div>
      );
   } else if (taskType === BoardsTaskType.EQUIPMENT_ASSIGNMENT) {
      return (
         <div>
            <Flex gap="sm" alignItems="center" paddingLeft="xs">
               <Excavator size="md" />
               <Flex direction="column" gap="xs">
                  <Typography className="task-card-header-equipment-name">
                     {taskRecord.getData("equipment_name")}
                  </Typography>
                  <Typography intent="small" className="task-card-body-content-row-item">
                     {taskRecord.getData("equipment_type_name")}
                  </Typography>
               </Flex>
            </Flex>
         </div>
      );
   } else if (taskType === BoardsTaskType.EQUIPMENT_REQUEST) {
      // TODO: implement copilot icon when we add equipment to the smart bench
      return (
         <Flex
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            padding="sm"
            style={{ width: "100%" }}
            className="task-card-header-request task-card-header-request--equipment"
         >
            <Excavator size="md" />
            <Pill color="blue">{I18n.t("views.company.workforce_planning.request.request")}</Pill>
         </Flex>
      );
   } else return null;
}

function getSensitiveFieldValue(
   fieldName: string,
   taskValue: string | null,
   sensitiveFields: string[],
   canViewSensitive: boolean,
): string | null {
   return sensitiveFields.includes(fieldName) && !canViewSensitive ? null : taskValue;
}

export function TaskCardBodyContent({
   taskRecord,
   taskBoardRef,
   isSelected = false,
}: {
   taskRecord: any;
   taskBoardRef: React.RefObject<any>;
   isSelected?: boolean;
}) {
   const I18n = useI18nContext();
   useTaskUpdate(taskRecord);
   const config = useBoardsConfig();
   const { checkAuthAction } = usePermissionContext();
   const canViewPeopleSensitiveFields = checkAuthAction(AuthAction.VIEW_PEOPLE_SENSITIVE);
   const peopleSensitiveFields = authManager.peopleSensitiveFields();

   if (taskRecord.getData("type") === BoardsTaskType.CATEGORY) {
      return null;
   }

   const task = taskRecord;
   const project = taskBoardRef.current.instance.columns.getById(task.getData("project_id"));
   const paidShiftHours = taskBoardRef.current.instance.company.paid_shift_hours;
   const overtimeDayRates = taskBoardRef.current.instance.company.overtime_day_rates;
   const selectedDate = new Date(
      taskBoardRef.current.instance.project.transport.load.params.day_filter,
   );
   const { dailyRate } = getDailyRateAndBurn(
      project,
      [task.originalData],
      paidShiftHours,
      overtimeDayRates,
      selectedDate,
   );
   const dailyRateString = `${dailyRate.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
   })}`;
   const phoneNumber = task.getData("phone_number")
      ? formatPhoneNumberForTaskCard(task.getData("phone_number"))
      : "";
   const totalAssignmentRange = getDatesRangeString(
      getAttachedDate(task.getData("start_day")),
      getAttachedDate(task.getData("end_day")),
      I18n,
   );

   // Due to potential bad prod data, we need to check for existence and default to empty string if not present
   let allocationInformation = "";
   if (task.getData("percent_allocated")) {
      allocationInformation = `${task.getData("percent_allocated")}% Allocated`;
   } else {
      const startTime = task.getData("start_time");
      const endTime = task.getData("end_time");
      if (startTime && endTime) {
         const startString = formatTimeString(task.getData("start_time"));
         const endString = formatTimeString(task.getData("end_time"));
         allocationInformation = `${startString} - ${endString}`;
      }
   }

   const taskType = task.getData("type");
   if (taskType === BoardsTaskType.REQUEST || taskType === BoardsTaskType.EQUIPMENT_REQUEST) {
      const equipmentTypeNameOrJobTitle =
         taskType === BoardsTaskType.EQUIPMENT_REQUEST
            ? task.getData("equipment_type_name")
            : task.getData("job_title_name");
      return (
         <div className={`task-card-body-content ${isSelected ? "task-card-selected" : ""}`}>
            <div className="task-card-body-content-row">
               {equipmentTypeNameOrJobTitle && (
                  <Typography className="task-card-body-content-row-item" intent="small">
                     {equipmentTypeNameOrJobTitle}
                  </Typography>
               )}
            </div>
            {config.showTags && task.getData("request_tags") && (
               <div className="task-card-body-content-row">
                  <TagDisplay tags={task.getData("request_tags")} />
               </div>
            )}
            {config.showTotalAssignmentRange && (
               <div className="task-card-body-content-row">
                  <Typography className="task-card-body-content-row-item" intent="small">
                     {totalAssignmentRange}
                  </Typography>
               </div>
            )}
            {config.showAllocationInformation && (
               <div className="task-card-body-content-row">
                  <Typography className="task-card-body-content-row-item" intent="small">
                     {allocationInformation}
                  </Typography>
               </div>
            )}
         </div>
      );
   } else {
      const isResourceAssignment = taskType === BoardsTaskType.ASSIGNMENT;
      return (
         <div className={`task-card-body-content ${isSelected ? "task-card-selected" : ""}`}>
            {isResourceAssignment && config.showPhoneNumber && (
               <div className="task-card-body-content-row">
                  <Typography className="task-card-body-content-row-item" intent="small">
                     {phoneNumber || "--"}
                  </Typography>
               </div>
            )}
            {isResourceAssignment && config.showTags && (
               <div className="task-card-body-content-row">
                  {task.getData("resource_tags") ? (
                     <TagDisplay tags={task.getData("resource_tags")} />
                  ) : (
                     <Typography className="task-card-body-content-row-item" intent="small">
                        {"--"}
                     </Typography>
                  )}
               </div>
            )}
            {config.showTotalAssignmentRange && (
               <div className="task-card-body-content-row">
                  <Typography className="task-card-body-content-row-item" intent="small">
                     {totalAssignmentRange || "--"}
                  </Typography>
               </div>
            )}
            {config.showAllocationInformation && (
               <div className="task-card-body-content-row">
                  <Typography className="task-card-body-content-row-item" intent="small">
                     {allocationInformation || "--"}
                  </Typography>
               </div>
            )}
            {config.showRateInfo && (
               <div className="task-card-body-content-row">
                  <Typography className="task-card-body-content-row-item" intent="small">
                     {dailyRate ? `$${dailyRateString}/hour` : "--"}
                  </Typography>
               </div>
            )}
            {isResourceAssignment && config.showAddress && (
               <div className="task-card-body-content-row">
                  <AddressDisplay
                     address1={getSensitiveFieldValue(
                        "address_1",
                        task.getData("resource_address_1"),
                        peopleSensitiveFields,
                        canViewPeopleSensitiveFields,
                     )}
                     address2={getSensitiveFieldValue(
                        "address_2",
                        task.getData("resource_address_2"),
                        peopleSensitiveFields,
                        canViewPeopleSensitiveFields,
                     )}
                     cityTown={getSensitiveFieldValue(
                        "city_town",
                        task.getData("resource_city_town"),
                        peopleSensitiveFields,
                        canViewPeopleSensitiveFields,
                     )}
                     stateProvince={getSensitiveFieldValue(
                        "state_province",
                        task.getData("resource_state_province"),
                        peopleSensitiveFields,
                        canViewPeopleSensitiveFields,
                     )}
                     zipcode={getSensitiveFieldValue(
                        "zipcode",
                        task.getData("resource_zipcode"),
                        peopleSensitiveFields,
                        canViewPeopleSensitiveFields,
                     )}
                  />
               </div>
            )}
         </div>
      );
   }
}
